import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CompanyInterface } from "../../../../models/GeneralModule/Company";
import companyServices from "../../../../services/general/companyServices";
import { CACHE_KEY_COMPANY } from "../../../../utils/constants";

interface UpdateCompaniesContext {
  previousCompanies: CompanyInterface[];
}

const useUpdateCompany = (page: number, pageSize: number) => {
  const queryClient = useQueryClient();

  return useMutation<
    CompanyInterface,
    Error,
    CompanyInterface,
    UpdateCompaniesContext
  >({
    mutationFn: async (company: CompanyInterface) => {
      // Assuming the ID is stored in the company object or can be derived
      const { id, ...companyData } = company;
      return companyServices.Update(id || "", companyData);
    },

    onMutate: async (updatedCompany: CompanyInterface) => {
      await queryClient.cancelQueries({
        queryKey: [
          CACHE_KEY_COMPANY,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
      });

      const previousCompanies =
        queryClient.getQueryData<CompanyInterface[]>([
          CACHE_KEY_COMPANY,
          page,
          pageSize,
        ]) || [];

      queryClient.setQueryData<CompanyInterface[]>(
        [
          CACHE_KEY_COMPANY,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        (companies = []) =>
          companies.map((company) =>
            company.id === updatedCompany.id ? updatedCompany : company
          )
      );

      return { previousCompanies };
    },

    onSuccess: (savedDepartment) => {
      queryClient.setQueryData<CompanyInterface[]>(
        [
          CACHE_KEY_COMPANY,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        (companies) =>
          companies?.map((company) =>
            company.id === savedDepartment.id ? savedDepartment : company
          )
      );
    },

    onError: (error, updatedCompany, context) => {
      if (!context) return;

      queryClient.setQueryData<CompanyInterface[]>(
        [
          CACHE_KEY_COMPANY,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        context.previousCompanies
      );
    },
  });
};

export default useUpdateCompany;
